import { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Table,
  Center,
  Loader,
  Group,
  Pagination,
  Select,
  ActionIcon,
  Text,
  Checkbox,
  Button,
  Paper,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useLocation } from "wouter";
import { getAdminJobs, requeueJobs } from "../api";
import JobStatusBadge from "./JobStatusBadge";
import { IconRefresh } from "@tabler/icons-react";

const formatJobType = (type) =>
  type?.replace(/Job$/, "").replace(/([A-Z])/g, " $1").trim() || type;

const formatTimestamp = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) +
    " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};

const truncateId = (id) => id?.slice(0, 8) + "…" || "—";

export default function JobList() {
  const [, setLocation] = useLocation();
  const [jobs, setJobs] = useState(null);
  const [tenantFilter, setTenantFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [forceRefresh, setForceRefresh] = useState(0);
  // Selection persists across pages (ids), so you can sweep up failed jobs across
  // several pages and re-queue them all in one go.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [isRequeuing, setIsRequeuing] = useState(false);
  const pageSize = 20;

  useEffect(() => {
    setIsLoading(true);
    getAdminJobs(
      activePage,
      pageSize,
      tenantFilter ?? "",
      statusFilter ?? ""
    ).then((data) => {
      setJobs(data);
      setIsLoading(false);
    });
  }, [activePage, statusFilter, tenantFilter, forceRefresh]);

  const toggleOne = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const pageIds = jobs?.data.map((p) => p.job.id) ?? [];
  const selectedOnPage = pageIds.filter((id) => selectedIds.has(id)).length;
  const allOnPageSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;

  const togglePage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleRequeue = (hard) => {
    const ids = [...selectedIds];
    if (ids.length === 0) return;
    setIsRequeuing(true);
    requeueJobs(ids, hard)
      .then((res) => {
        const failed = res?.failedIds?.length ?? 0;
        notifications.show({
          title: hard ? "Jobs re-queued (HARD)" : "Jobs re-queued",
          message:
            `${res?.succeeded ?? ids.length} of ${res?.requested ?? ids.length} re-queued` +
            (failed ? ` — ${failed} failed` : ""),
          color: failed ? "yellow" : "green",
        });
        clearSelection();
        setForceRefresh((v) => v + 1);
      })
      .catch((error) => {
        notifications.show({
          title: "Failed to re-queue jobs",
          message: "" + error,
          color: "red",
        });
      })
      .finally(() => setIsRequeuing(false));
  };

  if (!jobs)
    return (
      <Center mt="xl">
        <Loader size="xl" />
      </Center>
    );

  const selectedCount = selectedIds.size;

  return (
    <Container size="xl">
      <Grid justify="space-between" gap="2px">
        <Grid.Col span={12}>
          <Group>
            <Pagination
              hideWithOnePage={false}
              total={Math.max(1, Math.ceil(jobs.totalCount / pageSize))}
              value={activePage}
              onChange={setActivePage}
            />
            {isLoading ? (
              <Loader size="sm" />
            ) : (
              <ActionIcon onClick={() => setForceRefresh(forceRefresh + 1)}>
                <IconRefresh size={16} />
              </ActionIcon>
            )}

            <Select
              clearable
              value={statusFilter}
              onChange={setStatusFilter}
              data={["success", "processing", "queued", "failed"]}
            />
          </Group>

          {selectedCount > 0 && (
            <Paper withBorder p="xs" mt="sm" radius="md">
              <Group justify="space-between">
                <Text fw={500} fz="sm">
                  {selectedCount} selected
                </Text>
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="subtle"
                    color="gray"
                    onClick={clearSelection}
                    disabled={isRequeuing}
                  >
                    Clear
                  </Button>
                  <Button
                    size="xs"
                    color="blue"
                    loading={isRequeuing}
                    onClick={() => handleRequeue(false)}
                  >
                    Re-Queue {selectedCount}
                  </Button>
                  <Button
                    size="xs"
                    color="red"
                    variant="light"
                    loading={isRequeuing}
                    onClick={() => handleRequeue(true)}
                  >
                    Re-Queue HARD
                  </Button>
                </Group>
              </Group>
            </Paper>
          )}

          <Table stickyHeader stickyHeaderOffset={60}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 40 }}>
                  <Checkbox
                    aria-label="Select all on this page"
                    checked={allOnPageSelected}
                    indeterminate={selectedOnPage > 0 && !allOnPageSelected}
                    onChange={togglePage}
                  />
                </Table.Th>
                <Table.Th>Type</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Started</Table.Th>
                <Table.Th>NextRun</Table.Th>
                <Table.Th>RetryCount</Table.Th>
                <Table.Th>Tenant</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {jobs.data.map((p) => (
                <Table.Tr
                  style={{ cursor: "pointer" }}
                  bg={selectedIds.has(p.job.id) ? "var(--mantine-color-blue-light)" : undefined}
                  onClick={() => setLocation(`jobs/${p.job.id}`)}
                  key={p.job.id}
                >
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      aria-label="Select job"
                      checked={selectedIds.has(p.job.id)}
                      onChange={() => toggleOne(p.job.id)}
                    />
                  </Table.Td>
                  <Table.Td>{formatJobType(p.type)}</Table.Td>
                  <Table.Td>
                    <JobStatusBadge status={p.job.status} />
                  </Table.Td>
                  <Table.Td>{formatTimestamp(p.job.started)}</Table.Td>
                  <Table.Td>{formatTimestamp(p.job.nextRun)}</Table.Td>
                  <Table.Td>{p.job.retryCount}</Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed" title={p.job.tenantId}>
                      {truncateId(p.job.tenantId)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Grid.Col>
      </Grid>
    </Container>
  );
}
